"use client";
import { createUrlWithSearchParams } from "@/shared/libs/url-utils";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useState } from "react";

export const useUrlFilters = (filtersToReset: string[]) => {
  const pathName = usePathname();
  const params = useSearchParams();
  const { replace } = useRouter();
  const [pendingFilters, setPendingFilters] = useState<Record<string, string>>(
    {}
  );
  const [isSubmittingFilter, submitFilter] = useState(false);

  const resetFilters = useCallback(() => {
    const searchParams = new URLSearchParams(params.toString());
    for (const filter of filtersToReset) {
      searchParams.delete(filter);
    }
    replace(createUrlWithSearchParams(pathName, searchParams));
  }, [pathName, params, replace, filtersToReset]);

  const changeFilter = useCallback(
    (key: string) => (value: string) => () => {
      setPendingFilters((prev) => {
        const newFilters = { ...prev, [key]: value };
        submitFilter(
          Object.entries(newFilters).some(
            ([k, v]) => params.get(k) !== v || (!v && params.has(k))
          )
        );
        return newFilters;
      });
    },
    [params]
  );

  const handleApplyFilters = useCallback(() => {
    const searchParams = new URLSearchParams(params.toString());
    Object.entries(pendingFilters).forEach(([key, value]) => {
      value ? searchParams.set(key, value) : searchParams.delete(key);
    });
    replace(createUrlWithSearchParams(pathName, searchParams));
    submitFilter(false);
  }, [pendingFilters, pathName, params, replace]);

  const hasActiveFilters = useCallback(
    () => filtersToReset.some((key) => params.has(key)),
    [params, filtersToReset]
  );

  const getCurrentFilterState = useCallback(
    (key: string) => pendingFilters[key] || params.get(key) || "",
    [pendingFilters, params]
  );

  return {
    isSubmittingFilter,
    resetFilters,
    changeFilter,
    handleApplyFilters,
    hasActiveFilters,
    getCurrentFilterState,
  };
};
